import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  House,
  Briefcase,
  FileText,
  Sparkle,
  UsersThree,
  PlugsConnected,
  ChatCircleText,
  SignOut,
  List,
  X,
  IdentificationCard,
} from "@phosphor-icons/react";
import ChatDrawer from "@/components/chat/ChatDrawer";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, end: true },
  { to: "/clients", label: "Clients", icon: IdentificationCard },
  { to: "/engagements", label: "Engagements", icon: Briefcase },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/mini-apps", label: "Mini-Apps", icon: Sparkle },
  { to: "/team", label: "Team", icon: UsersThree },
  { to: "/integrations", label: "Integrations", icon: PlugsConnected },
  { to: "/messages", label: "Messages", icon: ChatCircleText },
];

const AppShell = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const SideNav = (
    <nav className="flex flex-col gap-1">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={() => setMobileOpen(false)}
          data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
              isActive
                ? "bg-[#1A2F24] text-[#F7F6F2]"
                : "text-[#1A2F24] hover:bg-[#efece3]"
            }`
          }
        >
          <item.icon size={18} weight="regular" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#F7F6F2] grid lg:grid-cols-[260px_1fr]">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col border-r border-[#1A2F24]/12 bg-white">
        <div className="p-6 border-b border-[#1A2F24]/12">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#1A2F24] flex items-center justify-center">
              <span className="text-[#F7F6F2] font-serif text-lg leading-none">L</span>
            </div>
            <div>
              <p className="font-serif text-lg text-[#112018] leading-none">Ledgerline</p>
              <p className="label-eyebrow leading-none mt-1">
                {user?.firm_name || "CPA Firm"}
              </p>
            </div>
          </div>
        </div>
        <div className="flex-1 p-3">{SideNav}</div>
        <div className="border-t border-[#1A2F24]/12 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#112018]">{user?.name}</p>
              <p className="label-eyebrow leading-none mt-1">{user?.role || "Partner"}</p>
            </div>
            <button
              data-testid="logout-btn"
              onClick={() => {
                logout();
                nav("/auth");
              }}
              className="p-2 hover:bg-[#efece3]"
              title="Sign out"
            >
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden flex items-center justify-between p-4 border-b border-[#1A2F24]/12 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#1A2F24] flex items-center justify-center">
            <span className="text-[#F7F6F2] font-serif text-base">L</span>
          </div>
          <p className="font-serif text-lg text-[#112018]">Ledgerline</p>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2"
        >
          {mobileOpen ? <X size={20} /> : <List size={20} />}
        </button>
      </header>

      {mobileOpen && (
        <div className="lg:hidden border-b border-[#1A2F24]/12 bg-white">
          <div className="p-3">{SideNav}</div>
          <div className="border-t border-[#1A2F24]/12 p-4 flex items-center justify-between">
            <p className="text-sm">{user?.name}</p>
            <button
              onClick={() => {
                logout();
                nav("/auth");
              }}
              className="btn-outline"
            >
              Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="min-h-screen relative">
        <div className="p-6 md:p-10 max-w-[1400px] mx-auto">
          <Outlet />
        </div>

        {/* Floating chat */}
        <button
          data-testid="open-chat-fab"
          onClick={() => setChatOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-[#1A2F24] text-[#F7F6F2] flex items-center justify-center hover:bg-[#A8977B] transition-colors shadow-lg"
          title="Vellum AI Assistant"
        >
          <ChatCircleText size={22} weight="regular" />
        </button>

        <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
      </main>
    </div>
  );
};

export default AppShell;
